import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreVertIcon from '@mui/icons-material/MoreVert'

import axios from '../../dataClient/dataClient'
import { IconOnlyButton } from '../common/Button'
import { MedBodyText } from '../styles/Text'
import { theme } from '../../themes/theme'
import { buildBillPdfFileName, buildBillReceiptPdfFileName } from '../../util/billFormatting'
import { handleDownloadResponse } from '../../util/downloadFile'
import CancelBillPopUp from '../billActions/CancelBillPopUp'
import PayInPersonPopUp from '../billActions/PayInPersonPopUp'
import RefundBillPopUp from '../billActions/RefundBillPopUp'
import MarkRefundInPersonPopUp from '../billActions/MarkRefundInPersonPopUp'
import ResendReceiptPopUp from '../billActions/ResendReceiptPopUp'

const StyledMenu = styled(Menu)`
  .MuiPaper-root {
    min-width: 150px;
    border-radius: 5px;
    background-color: ${theme.colors.background.medium};
    box-shadow: none;
  }

  .MuiList-root {
    padding: 5px 0;
  }
`

const StyledMenuItem = styled(MenuItem)`
  padding: 8px 15px;
  text-align: right;
  font-family: ${theme.fonts.body};
  font-size: 14px;
  justify-content: flex-end;

  &:hover {
    background-color: ${theme.colors.primaryExtraLight};
  }
`

function SentBillRowMenu({ bill, requestorEmail = '', handleBillUpdate = () => {} }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [downloadingBill, setDownloadingBill] = useState(false)
  const [downloadingReceipt, setDownloadingReceipt] = useState(false)
  const [openPopUp, setOpenPopUp] = useState()

  const open = Boolean(anchorEl)

  const handleClick = event => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  async function handleDownloadBill() {
    setDownloadingBill(true)
    handleClose()

    try {
      const response = await axios.get(`/bill/${bill.id}/download`, { responseType: 'blob' })
      const fileName = buildBillPdfFileName(bill.secondaryId)
      handleDownloadResponse(response, fileName, 'download')
    } catch (e) {
      console.error('An error occurred while downloading the bill', bill.id, e)
    } finally {
      setDownloadingBill(false)
    }
  }

  async function handleDownloadReceipt() {
    setDownloadingReceipt(true)
    handleClose()

    try {
      const response = await axios.get(`/bill/${bill.id}/download-receipt`, { responseType: 'blob' })
      const fileName = buildBillReceiptPdfFileName(bill.secondaryId)
      handleDownloadResponse(response, fileName, 'download')
    } catch (e) {
      console.error('An error occurred while downloading the receipt', bill.id, e)
    } finally {
      setDownloadingReceipt(false)
    }
  }

  function handleSuccess() {
    handleClose()
    handleBillUpdate()
  }

  function handleOpenPopUp(value) {
    setOpenPopUp(value)
    handleClose()
  }

  return (
    <>
      <IconOnlyButton
        onClick={handleClick}
        aria-label="Bill menu"
        aria-controls={open ? 'billing-row-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disabled={downloadingBill || downloadingReceipt}>
        <MoreVertIcon />
      </IconOnlyButton>
      <StyledMenu
        id="billing-row-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        slotProps={{
          list: {
            'aria-labelledby': 'bill-menu-button'
          }
        }}>
        {(bill.status === 'PAID' || bill.status === 'REFUNDED') && requestorEmail !== '' && bill.paymentMethod !== 'IN_PERSON' && (
          <StyledMenuItem onClick={() => handleOpenPopUp('resendReceipt')}>
            <MedBodyText>Resend receipt</MedBodyText>
          </StyledMenuItem>
        )}
        {bill.status === 'PAID' && requestorEmail && bill.paymentMethod !== 'IN_PERSON' && (
          <StyledMenuItem onClick={() => handleOpenPopUp('refund')}>
            <MedBodyText>Refund</MedBodyText>
          </StyledMenuItem>
        )}
        {bill.status === 'PAID' && bill.paymentMethod === 'IN_PERSON' && (
          <StyledMenuItem onClick={() => handleOpenPopUp('refundInPerson')}>
            <MedBodyText>Mark refund in person</MedBodyText>
          </StyledMenuItem>
        )}
        <StyledMenuItem onClick={downloadingBill ? undefined : handleDownloadBill} disabled={downloadingBill}>
          <MedBodyText>Download</MedBodyText>
        </StyledMenuItem>
        {(bill.status === 'PAID' || bill.status === 'REFUNDED') && (
          <StyledMenuItem onClick={downloadingReceipt ? undefined : handleDownloadReceipt} disabled={downloadingReceipt}>
            <MedBodyText>Download receipt</MedBodyText>
          </StyledMenuItem>
        )}
        {bill.status === 'WAITING' && (
          <StyledMenuItem onClick={() => handleOpenPopUp('cancel')}>
            <MedBodyText>Cancel</MedBodyText>
          </StyledMenuItem>
        )}
        {bill.status === 'WAITING' && (
          <StyledMenuItem onClick={() => handleOpenPopUp('payInPerson')}>
            <MedBodyText>Mark paid in person</MedBodyText>
          </StyledMenuItem>
        )}
      </StyledMenu>
      <CancelBillPopUp
        billId={bill.id}
        billType={bill.type}
        secondaryId={bill.secondaryId}
        amount={bill.total}
        open={openPopUp === 'cancel'}
        onClose={() => setOpenPopUp()}
        onSuccess={handleSuccess}
      />
      <PayInPersonPopUp
        billId={bill.id}
        secondaryId={bill.secondaryId}
        amount={bill.amountDue}
        open={openPopUp === 'payInPerson'}
        onClose={() => setOpenPopUp()}
        onSuccess={handleSuccess}
      />
      {requestorEmail && (
        <RefundBillPopUp
          billId={bill.id}
          billType={bill.type}
          secondaryId={bill.secondaryId}
          amount={bill.total}
          email={requestorEmail}
          open={openPopUp === 'refund'}
          onClose={() => setOpenPopUp()}
          onSuccess={handleSuccess}
        />
      )}
      <MarkRefundInPersonPopUp
        billId={bill.id}
        secondaryId={bill.secondaryId}
        open={openPopUp === 'refundInPerson'}
        onClose={() => setOpenPopUp()}
        onSuccess={handleSuccess}
      />
      {bill.paymentMethod !== 'IN_PERSON' && (
        <ResendReceiptPopUp
          billId={bill.id}
          billType={bill.type}
          secondaryBillId={bill.secondaryId}
          email={requestorEmail}
          open={openPopUp === 'resendReceipt'}
          onClose={() => setOpenPopUp()}
          onSuccess={handleSuccess}
        />
      )}
    </>
  )
}

SentBillRowMenu.propTypes = {
  bill: PropTypes.shape({
    id: PropTypes.string.isRequired,
    secondaryId: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['WAITING', 'PAID', 'CANCELLED', 'REFUNDED', 'ABANDONED', 'REJECTED']).isRequired,
    type: PropTypes.oneOf(['APPLICATION_FEE', 'FEE_ESTIMATE', 'INVOICE']).isRequired,
    total: PropTypes.number.isRequired,
    amountDue: PropTypes.number.isRequired,
    paymentMethod: PropTypes.oneOf(['ONLINE', 'IN_PERSON'])
  }).isRequired,
  requestorEmail: PropTypes.string
}

export default SentBillRowMenu
